/**
 * Copyright by:
 * by ESS-Ehrhardt Software Solutions & Service GmbH
 * Basselscheider Str. 55
 * D-56281 Emmelshausen
 * +49 (0) 67 42 / 87 27 0
 * info@epg.com
 */

import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  GanttChartData,
  GanttSegment,
  GanttSegmentType,
  GANTT_COLORS,
  GANTT_LABELS
} from './gantt-chart.models';

@Component({
  selector: 'app-gantt-chart',
  templateUrl: './gantt-chart.component.html',
  styleUrls: ['./gantt-chart.component.scss'],
  standalone: false
})
export class GanttChartComponent implements OnInit, OnChanges {
  @Input() data: GanttChartData = {
    total: 0,
    finished: 0,
    inProgress: 0,
    open: 0,
    overdue: 0,
    overdueNew: 0,
    overdueInProgress: 0,
    scheduled: 0,
    withIncidents: 0
  };

  @Input() height: string = '10px';
  @Input() showLabels: boolean = false;
  @Input() showTooltips: boolean = true;
  @Input() borderRadius: string = '4px';

  @Output() segmentClick = new EventEmitter<GanttSegment>();

  segments: GanttSegment[] = [];
  isAnimating = false;
  animationClass = 'gantt-chart-loading';
  private hasAnimated = false;

  tasksText = $localize`:@@tasks:tasks`;
  percentText = $localize`:@@percentOfTotal:percent of total`;

  ngOnInit(): void {
    this.updateSegments();

    if (this.segments.length > 0 && !this.hasAnimated) {
      setTimeout(() => {
        this.triggerLoadAnimation();
        this.hasAnimated = true;
      }, 10);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.animationClass = 'gantt-chart-loading';
      this.updateSegments();

      if (this.segments.length > 0) {
        setTimeout(() => {
          this.triggerLoadAnimation();
        }, 10);
      }
    }
  }

  private updateSegments(): void {
    if (!this.data || this.data.total === 0) {
      this.segments = [];
      this.animationClass = '';
      return;
    }

    const segments: GanttSegment[] = [];

    const ganttTotal =
      this.data.finished +
      this.data.inProgress +
      this.data.overdueInProgress +
      this.data.open +
      this.data.overdueNew +
      this.data.scheduled;

    if (ganttTotal === 0) {
      this.segments = [];
      this.animationClass = '';
      return;
    }

    segments.push({
      label: GANTT_LABELS[GanttSegmentType.FINISHED],
      value: this.data.finished,
      percentage: this.data.finished > 0 ? (this.data.finished / ganttTotal) * 100 : 0,
      color: GANTT_COLORS[GanttSegmentType.FINISHED],
      type: GanttSegmentType.FINISHED
    });

    // In Progress segment with overdue in-progress split
    const inProgressCombinedValue = this.data.inProgress + this.data.overdueInProgress;
    const inProgressCombinedPercentage =
      inProgressCombinedValue > 0 ? (inProgressCombinedValue / ganttTotal) * 100 : 0;
    const inProgressPercentage =
      inProgressCombinedValue > 0 && this.data.inProgress > 0
        ? (this.data.inProgress / inProgressCombinedValue) * 100
        : 0;
    const overdueInProgressPercentage =
      inProgressCombinedValue > 0 && this.data.overdueInProgress > 0
        ? (this.data.overdueInProgress / inProgressCombinedValue) * 100
        : 0;

    segments.push({
      label: $localize`:@@inProgressLabel:In Progress / Overdue In Progress`,
      value: inProgressCombinedValue,
      percentage: inProgressCombinedPercentage,
      color: GANTT_COLORS[GanttSegmentType.IN_PROGRESS],
      type: GanttSegmentType.IN_PROGRESS,
      isSplit: true,
      splitData: {
        left: {
          type: GanttSegmentType.IN_PROGRESS,
          value: this.data.inProgress,
          percentage: inProgressPercentage,
          color: GANTT_COLORS[GanttSegmentType.IN_PROGRESS],
          label: GANTT_LABELS[GanttSegmentType.IN_PROGRESS]
        },
        right: {
          type: GanttSegmentType.OVERDUE,
          value: this.data.overdueInProgress,
          percentage: overdueInProgressPercentage,
          color: GANTT_COLORS[GanttSegmentType.OVERDUE],
          label: GANTT_LABELS[GanttSegmentType.OVERDUE]
        }
      }
    });

    // Open segment with overdue new split
    const openCombinedValue = this.data.open + this.data.overdueNew;
    const openCombinedPercentage =
      openCombinedValue > 0 ? (openCombinedValue / ganttTotal) * 100 : 0;
    const openPercentage =
      openCombinedValue > 0 && this.data.open > 0 ? (this.data.open / openCombinedValue) * 100 : 0;
    const overdueNewPercentage =
      openCombinedValue > 0 && this.data.overdueNew > 0
        ? (this.data.overdueNew / openCombinedValue) * 100
        : 0;

    segments.push({
      label: $localize`:@@openLabel:Open / Overdue New`,
      value: openCombinedValue,
      percentage: openCombinedPercentage,
      color: GANTT_COLORS[GanttSegmentType.OPEN],
      type: GanttSegmentType.OPEN,
      isSplit: true,
      splitData: {
        left: {
          type: GanttSegmentType.OPEN,
          value: this.data.open,
          percentage: openPercentage,
          color: GANTT_COLORS[GanttSegmentType.OPEN],
          label: GANTT_LABELS[GanttSegmentType.OPEN]
        },
        right: {
          type: GanttSegmentType.OVERDUE_NEW,
          value: this.data.overdueNew,
          percentage: overdueNewPercentage,
          color: GANTT_COLORS[GanttSegmentType.OVERDUE_NEW],
          label: GANTT_LABELS[GanttSegmentType.OVERDUE_NEW]
        }
      }
    });

    // Scheduled segment
    segments.push({
      label: GANTT_LABELS[GanttSegmentType.SCHEDULED],
      value: this.data.scheduled,
      percentage: this.data.scheduled > 0 ? (this.data.scheduled / ganttTotal) * 100 : 0,
      color: GANTT_COLORS[GanttSegmentType.SCHEDULED],
      type: GanttSegmentType.SCHEDULED
    });

    this.segments = segments;

    if (this.hasAnimated && segments.length > 0) {
      this.animationClass = 'gantt-chart-visible gantt-chart-updating';
    }
  }

  private triggerLoadAnimation(): void {
    this.isAnimating = true;

    requestAnimationFrame(() => {
      setTimeout(() => {
        this.animationClass = 'gantt-chart-loaded';

        setTimeout(() => {
          this.isAnimating = false;
          this.animationClass = 'gantt-chart-visible';
        }, 1200);
      }, 50);
    });
  }

  onSegmentClick(segment: GanttSegment): void {
    this.segmentClick.emit(segment);
  }

  onSplitSegmentClick(segment: GanttSegment, side: 'left' | 'right'): void {
    if (segment.isSplit && segment.splitData) {
      const splitSide = segment.splitData[side];
      const clickedSegment: GanttSegment = {
        label: splitSide.label,
        value: splitSide.value,
        percentage: splitSide.percentage,
        color: splitSide.color,
        type: splitSide.type
      };
      this.segmentClick.emit(clickedSegment);
    }
  }

  getTooltipText(segment: GanttSegment): string {
    if (!this.showTooltips) {
      return '';
    }
    return `${segment.label}: ${segment.value} ${this.tasksText} (${segment.percentage.toFixed(
      1
    )}%)`;
  }

  getAriaLabel(segment: GanttSegment): string {
    return `${segment.label}: ${segment.value} ${this.tasksText}, ${segment.percentage.toFixed(
      1
    )} ${this.percentText}`;
  }

  trackBySegment(index: number, segment: GanttSegment): string {
    return segment.type;
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement;
    if (
      target.classList.contains('gantt-segment') &&
      (event.key === 'Enter' || event.key === ' ')
    ) {
      event.preventDefault();
      const segmentIndex = Array.from(target.parentElement!.children).indexOf(target);
      if (this.segments[segmentIndex]) {
        this.onSegmentClick(this.segments[segmentIndex]);
      }
    }
  }
}
